export const APP_NAME = 'EventRipple'

export const SUPPORTED_LANGUAGES = ['nb', 'en'] as const
export type SupportedLanguage = (typeof SUPPORTED_LANGUAGES)[number]

export const DEFAULT_LANGUAGE: SupportedLanguage = 'nb'

export const THEMES = ['light', 'dark', 'system'] as const
export type Theme = (typeof THEMES)[number]

export const DEFAULT_THEME: Theme = 'system'

export const STORAGE_KEYS = {
  THEME: 'eventripple-theme',
  /** The colour palette option, `'classic'` or absent (Parley). The pre-paint script in
   *  `index.html` hardcodes this literal; keep the two in sync. */
  PALETTE: 'er:palette',
  /**
   * The tenant default side this DEVICE last resolved (`'dark'`, or absent).
   *
   * Deliberately separate from `THEME` and deliberately not a choice: it is the
   * device's memory of `brand_settings.default_theme` so a white-label tenant's
   * cold load paints its identity before first paint, the same job the brand
   * snapshot (`er:brand:<uid>`) does for colours. Writing the tenant default
   * into `THEME` instead would make `hasExplicitThemeChoice()` true and kill
   * the event mode lock — see `stores/theme.ts`.
   *
   * ⚠️ The pre-paint script in `index.html` hardcodes this literal (an inline
   * script cannot import). Keep the two in sync, as with `THEME` above.
   * Written by AppLayout only, and cleared the moment a resolved brand has no
   * stated dark default — so a shared browser self-corrects on the next load.
   */
  ORG_DEFAULT_THEME: 'er:org-theme',
  /**
   * The visitor's EXPLICIT language choice, and nothing else (N-13). Written
   * only by `setLanguageChoice`; i18next's detector caches nothing any more
   * (`caches: []`), so a value here always means a person picked it. See
   * `lib/language.ts` for the precedence this sits at the top of.
   */
  LANGUAGE: 'er:lang',
  /**
   * The key i18next used to cache DETECTED languages into. It holds a sniffed
   * `navigator` value for every visitor who has ever loaded the app, so it is
   * cleared on boot rather than read — see `retireLegacyLanguageCache`.
   */
  LEGACY_LANGUAGE_CACHE: 'i18nextLng',
} as const

export const LOCATION_TYPES = ['physical', 'digital', 'hybrid'] as const
export type LocationType = (typeof LOCATION_TYPES)[number]

/** An event description's length limit. Was 500 in the form only (no database limit); a live
 *  Nortura event already held 744 characters and could not be extended (24.10.2026). */
export const DESCRIPTION_MAX = 5000
